import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const DEFAULTS = {
  app_name: "Faro",
  tagline: "Tu segundo cerebro para documentos, datos y decisiones.",
  footer_text: "A product of Holistor",
  meta_title: "Faro — IA para emprendedores y contadores",
  meta_description: "",
  meta_keywords: "",
  og_title: "",
  og_description: "",
  og_image_url: "",
  og_url: "",
  twitter_card: "summary_large_image",
  twitter_site: "",
  twitter_image_url: "",
  favicon_url: "",
  logo_url: "",
  theme_color: "#0a1971",
};

const BrandingContext = createContext(DEFAULTS);

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function BrandingProvider({ children }) {
  const [branding, setBranding] = useState(DEFAULTS);

  const fetchBranding = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_BASE}/branding`);
      setBranding({ ...DEFAULTS, ...data });
    } catch {
      // fall back to defaults silently
    }
  }, []);

  useEffect(() => {
    fetchBranding();
    const onRefresh = () => fetchBranding();
    window.addEventListener("branding:refresh", onRefresh);
    return () => window.removeEventListener("branding:refresh", onRefresh);
  }, [fetchBranding]);

  // Apply client-side: <title>, favicon, theme-color, meta description.
  // (For SEO crawlers, the FastAPI catch-all does the heavy lifting.)
  useEffect(() => {
    if (branding.meta_title) document.title = branding.meta_title;
    const setMeta = (sel, attr, val) => {
      if (val == null) return;
      let el = document.querySelector(sel);
      if (!el) {
        el = document.createElement("meta");
        // The selector is in the form: meta[name="x"] or meta[property="y"]
        const m = sel.match(/meta\[(\w+)="([^"]+)"\]/);
        if (m) el.setAttribute(m[1], m[2]);
        document.head.appendChild(el);
      }
      el.setAttribute(attr, val);
    };
    setMeta('meta[name="description"]', "content", branding.meta_description);
    setMeta('meta[name="keywords"]', "content", branding.meta_keywords);
    setMeta('meta[property="og:title"]', "content", branding.og_title || branding.meta_title);
    setMeta('meta[property="og:description"]', "content", branding.og_description || branding.meta_description);
    setMeta('meta[property="og:image"]', "content", absoluteUrl(branding.og_image_url));
    setMeta('meta[property="og:site_name"]', "content", branding.app_name);
    setMeta('meta[name="twitter:card"]', "content", branding.twitter_card);
    setMeta('meta[name="twitter:site"]', "content", branding.twitter_site);
    setMeta('meta[name="twitter:image"]', "content", absoluteUrl(branding.twitter_image_url || branding.og_image_url));
    setMeta('meta[name="theme-color"]', "content", branding.theme_color);

    if (branding.favicon_url) {
      let link = document.querySelector('link[rel="icon"]');
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
      }
      link.href = absoluteUrl(branding.favicon_url);
    }
  }, [branding]);

  return (
    <BrandingContext.Provider value={branding}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  return useContext(BrandingContext);
}

/**
 * Resolves a possibly-relative URL (e.g. /api/static/branding/logo-abc.png)
 * to an absolute URL using REACT_APP_BACKEND_URL. Returns the input
 * unchanged if it's already absolute or empty.
 */
export function absoluteUrl(url) {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  return url;
}
